import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/auth/AuthContext";

const inputCls =
  "w-full bg-[#0a0a0c] border border-zinc-700 rounded p-3 text-white text-xs font-mono focus:border-[#d4af37] outline-none transition-colors";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5";

export function CheckGrid({ options, values, onToggle, testPrefix }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {options.map((opt) => {
        const active = values.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            data-testid={`${testPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`px-3 py-2 rounded border text-left text-[11px] font-mono transition-colors ${
              active
                ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]"
                : "bg-[#0a0a0c] border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            {active ? "✓ " : ""}{opt}
          </button>
        );
      })}
    </div>
  );
}

export function YesNo({ label, value, onChange, testPrefix }) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <div className="flex gap-2">
        {["yes", "no"].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            data-testid={`${testPrefix}-${v}`}
            className={`px-5 py-2 rounded border text-[11px] font-mono uppercase transition-colors ${
              value === v
                ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]"
                : "bg-[#0a0a0c] border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

export function FileInput({ label, file, onChange, testid }) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <label
        className="flex items-center justify-center gap-2 w-full border border-dashed border-zinc-600 hover:border-[#d4af37] rounded p-4 text-[11px] font-mono text-zinc-400 cursor-pointer transition-colors"
        data-testid={testid}
      >
        <input
          type="file"
          accept=".pdf,.doc,.docx"
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
        {file ? <span className="text-[#d4af37]">{file.name} ({Math.round(file.size / 1024)} KB)</span> : "Tap to attach — PDF, DOC, DOCX (max 10 MB)"}
      </label>
    </div>
  );
}

export function SubmitButton({ submitting, label, testid }) {
  return (
    <button
      type="submit"
      disabled={submitting}
      data-testid={testid}
      className="w-full py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25 disabled:opacity-60"
    >
      {submitting ? "Submitting..." : label}
    </button>
  );
}

export function SuccessBox({ message, onReset, testid }) {
  return (
    <div className="text-center py-10" data-testid={testid}>
      <div className="w-14 h-14 mx-auto mb-5 rounded-full border border-[#d4af37]/50 flex items-center justify-center">
        <svg className="w-7 h-7 text-[#d4af37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
      <p className="text-sm text-zinc-200 leading-relaxed max-w-md mx-auto whitespace-pre-line">{message}</p>
      <button type="button" onClick={onReset} data-testid={`${testid}-reset`}
        className="mt-6 px-6 py-2.5 rounded border border-[#d4af37]/40 text-[10px] font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors">
        Submit Another
      </button>
    </div>
  );
}

export const postMultipart = async (path, fields, files) => {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (Array.isArray(v)) v.forEach((x) => fd.append(k, x));
    else fd.append(k, v ?? "");
  }
  for (const [k, f] of Object.entries(files)) {
    if (f) fd.append(k, f);
  }
  return axios.post(`${API}${path}`, fd);
};

export { inputCls, labelCls, toast };
